import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Draggable from 'react-draggable';
import { engineHealth, runEngineMentor, ENGINE_MODEL_LABEL } from '../slm/engineBridge';
import { cloudLlm, CLOUD_PROVIDER_LABELS, CLOUD_MODELS, friendlyByokError, listCloudModels } from '../slm/cloudProviders';
import { loadByok, saveByok, clearByok, maskKey, looksLikeKey, byokIsRemembered } from '../lib/byokStore';
import { buildScenarioTreeSummary, guessActiveBranch } from '../lib/scenarioTreeSummary';
import { DEFAULT_SLM_MODELS, pickThinkingVerbs } from '../slm/models';
import { vocab } from '../slm/prompts';
import { useAuth } from '../contexts/AuthContext';
import theme from '../theme';
import Sparkle from './Sparkle';
import QuotaDialog from './QuotaDialog';
import ContactRequestWidget from './ContactRequestWidget';
import { API_BASE } from '../lib/apiBase';
import { useT, useLang } from '../i18n/langContext.js';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClone, faFileLines, faCloud, faBolt, faFeather, faKey,
  faUpRightAndDownLeftFromCenter, faDownLeftAndUpRightToCenter,
  faChevronDown, faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import "../css/Chatbot.css";

const TIER_STATIC = {
  cloud: { label: null,   model: 'Gemini 3 Flash',   badgeClass: '' },
  lite:  { label: 'Lite', model: 'Gemini Flash Lite', badgeClass: 'mentor-tier-badge--lite' },
};

// v1 ships the local engine only. The Gemini cloud/lite tiers, the tier-switch
// icons, and the backend they need are all masked (code kept, just unreachable)
// until v2. Flip this to re-enable them.
const CLOUD_TIERS_ENABLED = false;

// One-time installer for end users who don't have the repo (bundled
// llama-server, Windows autostart, Sparkle tray icon). Hosted on Cloudflare
// R2 (bucket "sinergiegenerative") — free egress, keeps the 334 MB file off
// Firebase Hosting. Re-upload a new build to the same key to ship it.
const ENGINE_DOWNLOAD_URL = 'https://pub-a21b98ca738f4feeb97bd149084db9fe.r2.dev/SinergieEngine-Setup.exe';

// Quick-reply buttons for the mode-choice question, instead of relying on
// free text: a phrase naming two modes at once (e.g. "mappare gli scenari
// futuri") is genuinely ambiguous to classify, so the choice is made
// unambiguous by construction here — each text hits exactly one mode's
// keyword regex (see modeRouter.js/pocketflow_agents.py's pick_mode). The
// conversation continues in free text normally after this one turn.
// `text` is sent into the chat history for the mode keyword regexes — it is
// LLM/router input, not UI copy, so it comes from the prompt vocab
// (vocab(lang).mode_choice_texts), whose texts are tested to hit their own
// mode in that language's mode_keywords; `label` is the button's display text.
const MODE_CHOICES = (t, lang) => {
  const texts = vocab(lang).mode_choice_texts;
  return [
    { label: t('chat.modeChoices.traditional'), text: texts.tradizionale, mode: "tradizionale" },
    { label: t('chat.modeChoices.mapping'), text: texts.mappatura, mode: "mappatura" },
    { label: t('chat.modeChoices.scenarios'), text: texts.scenari_futuri, mode: "scenari_futuri" },
  ];
};

const PHASE_META = (t) => ({
  esplorazione_ideazione: { name: t('chat.phaseMeta.esplorazione_ideazione'), color: "#8b5cf6" },
  prototipazione_iterazione: { name: t('chat.phaseMeta.prototipazione_iterazione'), color: "#ec4899" },
  integrazione_implementazione: { name: t('chat.phaseMeta.integrazione_implementazione'), color: "#0ea5e9" },
  evoluzione_scalabilita: { name: t('chat.phaseMeta.evoluzione_scalabilita'), color: "#f59e0b" },
  interazione_etica: { name: t('chat.phaseMeta.interazione_etica'), color: "#10b981" },
});

const MsgSection = ({ title, children }) => (
  <div className="mentor-block">
    <span className="mentor-block__title">{title}</span>
    {children}
  </div>
);

// Small round avatar leading each message row — the editorial reading style
// (avatar + plain text, no chat bubble) replaces the old colored/white
// bubble boxes. The user's own avatar matches ProfilePage.jsx exactly
// (tier-colored circle + first initial of the email, Fraunces) instead of
// a generic "U", so it reads as the same identity everywhere in the app.
const MessageAvatar = ({ sender, userInitial, userColor }) => (
  sender === 'user' ? (
    <span
      className="message-avatar message-avatar--user"
      style={{ backgroundColor: userColor, fontFamily: theme.home.display }}
    >
      {userInitial}
    </span>
  ) : (
    <span className="message-avatar message-avatar--bot">
      <Sparkle size={12} />
    </span>
  )
);

// Floating badge on the message bubble's corner instead of a chip row under
// the text — keeps the user/mentor turn sequence visually continuous, the
// referenced cards are one click away instead of always-expanded.
const CardRefBadge = ({ cards, isOpen, onToggle, t }) => (
  <div className="card-ref-badge-wrap">
    <button
      type="button"
      className={`card-ref-badge${isOpen ? ' is-open' : ''}`}
      onClick={onToggle}
      title={t('chat.cardRefBadge.title')}
      aria-label={t('chat.cardRefBadge.aria', { count: cards.length })}
    >
      <FontAwesomeIcon icon={faClone} />
      <span className="card-ref-badge__count">{cards.length}</span>
    </button>
    {isOpen && (
      <div className="card-ref-popup">
        <span className="card-ref-popup__label">{t('chat.cardRefBadge.popupLabel')}</span>
        <div className="chip-row">
          {cards.map((c, i) => <span key={i} className="card-chip">{c.label}</span>)}
        </div>
      </div>
    )}
  </div>
);

const Chatbot = ({ sessionId, usedCards, unusedCards, onCardAction, onEvidence, onSplitAction, scenarioSplits, scenarioBranches, onGenerateBrief, onBrief, initialBrief, onPhase, initialPhase, onMode, initialMode, onModeAskAttempts, initialModeAskAttempts, onPendingCheckpoint, initialPendingCheckpoint, onTeacherStage, initialTeacherStage, onMessagesChange, initialMessages, onRequestSaveSession }) => {
  const t = useT();
  const { lang } = useLang();
  const { currentUser, role, isPro } = useAuth();
  // Mirrors ProfilePage.jsx's own avatar exactly (same tier/color/initial
  // logic) so the user sees the same identity mark in the mentor chat.
  const userTier = role === 'Admin' ? 'Admin' : (isPro ? 'Pro' : 'Free');
  const userColor = { Admin: '#d32f2f', Pro: theme.home.cyanText, Free: '#6b7280' }[userTier];
  const userInitial = (currentUser?.email || '?').charAt(0).toUpperCase();
  const [messages, setMessages] = useState(initialMessages || []);
  const [input, setInput] = useState('');
  const [isOpen, setIsOpen] = useState(true);
  const [isDocked, setIsDocked] = useState(false);
  const [purpose, setPurpose] = useState('');
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorPopup, setErrorPopup] = useState(null);
  const [openCardBadge, setOpenCardBadge] = useState(null);
  const [modelTier, setModelTier] = useState('local'); // 'cloud'|'local'|'lite'
  const [quotaDialogMode, setQuotaDialogMode] = useState(null); // null|'chat'
  const [quotaDialogReason, setQuotaDialogReason] = useState('quota'); // 'quota' (cloud tier only)
  const [slmProgress, setSlmProgress] = useState(null); // null | { text, progress 0-1 }
  const [longWait, setLongWait] = useState(false); // true once a turn has taken >1.5s with no real progress yet
  const [slmModel, setSlmModel] = useState(DEFAULT_SLM_MODELS[0]); // { modelId, label, size } — still shown in the quota dialog
  const [mentorVerbsOverride, setMentorVerbsOverride] = useState(null); // raw appConfig/global.mentorVerbs (Italian, admin-editable)
  // Resolved loading-verb list for the current language: the admin override
  // only applies for lang==='it' (see pickThinkingVerbs's own comment);
  // recomputed whenever the language or the override changes.
  const thinkingVerbs = useMemo(
    () => pickThinkingVerbs({ lang, override: mentorVerbsOverride }),
    [lang, mentorVerbsOverride]
  );
  const [engineAvailable, setEngineAvailable] = useState(false);
  const [engineModelPresent, setEngineModelPresent] = useState(false);
  const [engineDownloadPct, setEngineDownloadPct] = useState(null); // 0-100 while the engine is pulling the model, else null
  const [engineModelLabel, setEngineModelLabel] = useState(null); // live label from /health — reflects the tray's model picker
  // BYOK cloud mentor: user's own Gemini/OpenAI key, browser-only (see byokStore).
  const [byok, setByok] = useState(() => loadByok()); // { provider, key } | null
  const [byokOff, setByokOff] = useState(false); // user chose "torna al locale" for this session
  const [byokPanelOpen, setByokPanelOpen] = useState(false);
  const [byokForm, setByokForm] = useState(() => ({ provider: loadByok()?.provider || 'gemini', key: '', remember: byokIsRemembered() }));
  const [byokFormError, setByokFormError] = useState(null);
  const [showKeyNudge, setShowKeyNudge] = useState(false);
  const nudgeDismissedRef = useRef(false);
  const useByok = !!(byok && byok.key && byok.provider) && !byokOff;
  const [byokModels, setByokModels] = useState(null); // live list from the provider, null = not loaded / failed
  // Provider model catalogs change often: ask the provider (with the user's
  // own key, browser -> provider only) whenever the panel shows an active key.
  useEffect(() => {
    if (!byokPanelOpen || !byok?.key) return undefined;
    const ctrl = new AbortController();
    setByokModels(null);
    listCloudModels({ provider: byok.provider, apiKey: byok.key, signal: ctrl.signal })
      .then(setByokModels)
      .catch(() => setByokModels(null));
    return () => ctrl.abort();
  }, [byokPanelOpen, byok?.provider, byok?.key]);
  // Serialized scenari_futuri branching tree — lets the mentor pick the
  // right node to fork from (an existing branch, not always "root") when
  // proposing a new suggested_splits. See scenarioTreeSummary.js.
  const scenarioTree = useMemo(
    () => buildScenarioTreeSummary(scenarioSplits || [], scenarioBranches || []),
    [scenarioSplits, scenarioBranches]
  );
  const [cloudEnabled, setCloudEnabled] = useState(false);
  const [brief, setBrief] = useState(initialBrief || null);
  const [briefOpen, setBriefOpen] = useState(false);
  const [phase, setPhase] = useState(initialPhase || "esplorazione_ideazione");
  const [sessionMode, setSessionMode] = useState(initialMode || null);
  const [modeAskAttempts, setModeAskAttempts] = useState(initialModeAskAttempts || 0);
  const [pendingCheckpoint, setPendingCheckpoint] = useState(initialPendingCheckpoint || false);
  const [teacherStage, setTeacherStage] = useState(initialTeacherStage || "osserva");
  const [thinkingVerb, setThinkingVerb] = useState(() => pickThinkingVerbs({ lang })[0]);
  const retryDataRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => { setBrief(initialBrief || null); }, [initialBrief]);
  useEffect(() => { setPhase(initialPhase || "esplorazione_ideazione"); }, [initialPhase]);
  useEffect(() => { setSessionMode(initialMode || null); }, [initialMode]);
  useEffect(() => { setModeAskAttempts(initialModeAskAttempts || 0); }, [initialModeAskAttempts]);
  useEffect(() => { setPendingCheckpoint(initialPendingCheckpoint || false); }, [initialPendingCheckpoint]);
  useEffect(() => { setTeacherStage(initialTeacherStage || "osserva"); }, [initialTeacherStage]);
  useEffect(() => { setMessages(initialMessages || []); }, [initialMessages]);

  // Pick a fresh thinking verb each time a new "reasoning" spell starts.
  useEffect(() => {
    if (isLoading) setThinkingVerb(thinkingVerbs[Math.floor(Math.random() * thinkingVerbs.length)]);
  }, [isLoading, thinkingVerbs]);

  // Keep the transcript pinned to the latest message/reply.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isLoading, slmProgress]);

  // Lift the live transcript up for the local session-draft backup — unlike
  // brief/phase (once per mentor turn), this fires on every send/receive.
  useEffect(() => {
    if (onMessagesChange) onMessagesChange(messages);
  }, [messages, onMessagesChange]);

  const refreshEngine = useCallback(
    () => engineHealth().then((h) => {
      setEngineAvailable(h.ok);
      setEngineModelPresent(h.ok && h.modelPresent);
      setEngineDownloadPct(h.ok ? h.downloadPct : null);
      setEngineModelLabel(h.ok ? (h.modelLabel || null) : null);
    }).catch(() => {
      setEngineAvailable(false);
      setEngineModelPresent(false);
      setEngineDownloadPct(null);
      setEngineModelLabel(null);
    }),
    []
  );

  useEffect(() => { refreshEngine(); }, [refreshEngine]);

  // Keep polling on the local tier while the engine isn't reachable yet OR the
  // model isn't downloaded yet — so starting sinergie-engine.exe after the page
  // is open flips the UI over on its own, and the download % keeps ticking.
  // Stop once the engine is up AND the model is present.
  useEffect(() => {
    if (useByok || modelTier !== 'local' || !isOpen || (engineAvailable && engineModelPresent)) return;
    const id = setInterval(refreshEngine, 3000);
    return () => clearInterval(id);
  }, [useByok, modelTier, engineAvailable, engineModelPresent, isOpen, refreshEngine]);

  useEffect(() => {
    getDoc(doc(getFirestore(), 'appConfig', 'global')).then((snap) => {
      if (!snap.exists()) return;
      const cfg = snap.data()?.slmModels;
      const models = cfg?.models?.length ? cfg.models : DEFAULT_SLM_MODELS;
      const defaultId = cfg?.default || models.find((m) => m.enabled)?.modelId;
      const found = models.find((m) => m.modelId === defaultId) || models[0];
      if (found) setSlmModel(found);
      const cloud = snap.data()?.cloud;
      setCloudEnabled(CLOUD_TIERS_ENABLED && !!cloud?.enabled);
      const verbs = snap.data()?.mentorVerbs;
      setMentorVerbsOverride(Array.isArray(verbs) && verbs.length ? verbs : null);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if ((!CLOUD_TIERS_ENABLED || !cloudEnabled) && modelTier !== 'local') setModelTier('local');
  }, [cloudEnabled, modelTier]);

  const DT_PHASES = ['esplorazione_ideazione', 'prototipazione_iterazione', 'integrazione_implementazione', 'evoluzione_scalabilita', 'interazione_etica'];

  const getIdToken = async () => {
    if (!currentUser) return null;
    try { return await currentUser.getIdToken(); } catch (e) { return null; }
  };

  // overrideText: programmatic sends (the mode-choice buttons below) pass
  // their own unambiguous text instead of reading the input box, so a
  // button click can't be confused with whatever the user was mid-typing.
  const handleSend = async (overrideText) => {
    const text = overrideText !== undefined ? overrideText : input;
    if (!text.trim()) return;
    const userMessage = { text, sender: 'user' };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);

    if (isFirstMessage) {
      setPurpose(text);
      setIsFirstMessage(false);
    }
    if (overrideText === undefined) setInput('');
    setIsLoading(true);
    const response = await sendCardData(updatedMessages);
    setIsLoading(false);
    if (response) processApiResponse(response, updatedMessages);
  };

  const sendCardData = async (updatedMessages, tierOverride) => {
    // Purely a UX escalation, not a load-detection heuristic (we can't
    // reliably know in advance whether a real download/init is about to
    // happen for either local transport): if nothing comes back — no
    // response AND no real onProgress event — within 1.5s, upgrade the
    // plain "..." dots to a clearer "still working" indicator with a
    // visible (indeterminate, since we have no real % yet) progress
    // animation. A fast turn (model already warm) never triggers this at
    // all, so the normal case is unchanged; a slow one — first load, a
    // version update, or WebLLM just being silent for a while before its
    // first progress event — no longer looks like nothing is happening.
    const longWaitTimer = setTimeout(() => setLongWait(true), 1500);
    // After ~20s of a slow LOCAL turn, surface the "connect your own key" nudge
    // once per session. Never while BYOK is already the route.
    const keyNudgeTimer = setTimeout(() => {
      if (!useByok && !nudgeDismissedRef.current) setShowKeyNudge(true);
    }, 20000);
    try {
    const tier = tierOverride ?? (useByok ? 'byok' : modelTier);
    // Deterministic hint for which existing scenario branch the latest
    // message is probably deepening — see guessActiveBranch's own comment.
    // Computed at send time (not memoized with scenarioTree) since it
    // depends on the just-typed message, not just the tree shape.
    const lastUserText = updatedMessages[updatedMessages.length - 1]?.text || '';
    const likelyBranchId = guessActiveBranch(lastUserText, scenarioBranches || []);

    // Local path — the native Sinergie Engine (a separate llama.cpp process
    // on 127.0.0.1:8787), NOT the in-browser WebLLM path. Don't preemptively
    // show "Inizializzazione…": that overrides the normal "sta ragionando…"
    // state on every turn even when the model is already loaded. Let real
    // onProgress events (from an actual download/load) drive it instead.
    if (tier === 'local' || tier === 'byok') {
      const usingByok = tier === 'byok';
      // Defensive guard for a race — the engine dropped between the last
      // poll and this send. Normally the setup panel is already showing and
      // the input is disabled when the engine is unreachable. BYOK doesn't
      // need the local engine at all.
      if (!usingByok && !engineAvailable) {
        setErrorPopup({
          code: 1003,
          message: t('chat.errors.engineUnreachable'),
        });
        return null;
      }
      try {
        const { user_label: userLabel, mentor_label: mentorLabel } = vocab(lang);
        const mentorArgs = {
          sessionId,
          lang,
          // Include BOTH user (.text) and mentor (.message) turns, role-labelled,
          // so the model sees the real conversation and continues it instead of
          // restarting ("Ciao, iniziamo") every turn.
          chat: updatedMessages
            .slice(-6)
            .map(m => `${m.sender === 'user' ? userLabel : mentorLabel}: ${m.text || m.message || ''}`)
            .join(' | '),
          purpose: purpose || updatedMessages[0]?.text || '',
          usedCards,
          unusedCards,
          prevBrief: brief,
          prevPhase: phase,
          prevMode: sessionMode,
          prevModeAskAttempts: modeAskAttempts,
          prevPendingCheckpoint: pendingCheckpoint,
          prevTeacherStage: teacherStage,
          scenarioTree,
          likelyBranchId,
        };
        const result = await runEngineMentor(mentorArgs, {
          onProgress: (p) => setSlmProgress(p), // { text, progress } already normalized
          t,
          ...(usingByok ? { llm: cloudLlm({ ...byok, t }) } : {}),
        });
        if (result && result.brief) { setBrief(result.brief); if (onBrief) onBrief(result.brief); }
        if (result && result.phase) { setPhase(result.phase); if (onPhase) onPhase(result.phase); }
        if (result && result.mode !== undefined) {
          setSessionMode(result.mode);
          if (onMode) onMode(result.mode);
        }
        if (result && result.modeAskAttempts !== undefined) {
          setModeAskAttempts(result.modeAskAttempts);
          if (onModeAskAttempts) onModeAskAttempts(result.modeAskAttempts);
        }
        if (result && result.pendingCheckpoint !== undefined) {
          setPendingCheckpoint(result.pendingCheckpoint);
          if (onPendingCheckpoint) onPendingCheckpoint(result.pendingCheckpoint);
        }
        if (result && result.teacherStage !== undefined) {
          setTeacherStage(result.teacherStage);
          if (onTeacherStage) onTeacherStage(result.teacherStage);
        }
        setSlmProgress(null);
        return result;
      } catch (e) {
        setSlmProgress(null);
        // BYOK provider failed (bad key, rate limit, network). Fall back to the
        // local engine for this turn if it's up; otherwise show the reason.
        if (usingByok && /^BYOK_/.test(e?.message || '')) {
          console.warn('[byok] cloud mentor failed, falling back to local:', e?.message);
          const reason = friendlyByokError(e?.message || '', t);
          if (engineAvailable) {
            setErrorPopup(null);
            return await sendCardData(updatedMessages, 'local');
          }
          setErrorPopup({
            code: 2001,
            message: t('chat.errors.byokFailed', { provider: CLOUD_PROVIDER_LABELS[byok?.provider] || '', reason }),
          });
          return null;
        }
        console.error('[engine] local mentor failed:', e?.message, e);
        // The bridge surfaces the engine's own diagnostics as the Error
        // message ("engine timeout", "engine HTTP 500", "VRAM insufficiente
        // per caricare il modello", …). Show it and offer a plain retry.
        // Re-probe: if the engine actually died, the next poll re-shows the
        // setup panel; if it was a transient timeout, nothing changes.
        refreshEngine();
        setErrorPopup({
          code: 2000,
          message: t('chat.errors.engineError', { message: e?.message || t('chat.errors.retryFallback') }),
          onRetry: async () => {
            setErrorPopup(null);
            setIsLoading(true);
            const res = await sendCardData(updatedMessages, 'local');
            setIsLoading(false);
            if (res) processApiResponse(res, updatedMessages);
          },
        });
        return null;
      }
    }

    // Cloud / lite path
    const idToken = await getIdToken();
    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`;

    const data = {
      session: sessionId,
      chat: updatedMessages.slice(-3).map(m => m.text || m.message || '').join(' | '),
      purpose: purpose || updatedMessages[0]?.text || '',
      cards: JSON.stringify({
        usedCards: usedCards.map(c => ({ id: c.id, label: c.label, group: c.group, image: c.image, descrizione_breve_it: c.descrizioneBreve })),
        unusedCards: unusedCards.map(c => ({ id: c.id, label: c.label, group: c.group, image: c.image, descrizione_breve_it: c.descrizioneBreve })),
      }),
      prevPhase: phase,
      prevBrief: brief ? JSON.stringify(brief) : '',
      prevMode: sessionMode || '',
      modeAskAttempts,
      pendingCheckpoint,
      prevTeacherStage: teacherStage,
      scenarioTree,
      likelyBranchId: likelyBranchId || '',
      lang,
      ...(tier === 'lite' ? { tier: 'lite' } : {}),
    };

    try {
      const apiResponse = await fetch(`${API_BASE}/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!apiResponse.ok) throw new Error(`HTTP ${apiResponse.status}`);
      return await apiResponse.json();
    } catch (error) {
      console.error('Error sending data to chatbot:', error);
      setErrorPopup({ code: 1000, message: t('chat.errors.backendUnreachable') });
      return null;
    }
    } finally {
      clearTimeout(longWaitTimer);
      clearTimeout(keyNudgeTimer);
      setLongWait(false);
      setShowKeyNudge(false);
    }
  };

  const processApiResponse = (response, updatedMessages) => {
    console.log('Full API Response:', response);

    if (response?.quotaExceeded) {
      retryDataRef.current = { type: 'chat', updatedMessages };
      setQuotaDialogReason('quota');
      setQuotaDialogMode('chat');
      return;
    }

    if (response && typeof response === 'object' && response.error) {
      setErrorPopup({ code: response.code || 999, message: response.message || t('chat.errors.unknownError') });
      return;
    }

    if (response && typeof response === 'object' && response.phase && response.message) {
      if (onCardAction && (response.suggested_cards?.length > 0 || response.suggested_notes?.length > 0)) {
        onCardAction(response.suggested_cards || [], response.suggested_notes || []);
      }
      if (onEvidence && response.evidences?.length > 0) onEvidence(response.evidences);
      if (onSplitAction && response.suggested_splits?.length > 0) onSplitAction(response.suggested_splits);
      if (response.brief) { setBrief(response.brief); if (onBrief) onBrief(response.brief); }
      setPhase(response.phase);
      if (onPhase) onPhase(response.phase);
      if (response.mode !== undefined) {
        setSessionMode(response.mode);
        if (onMode) onMode(response.mode);
      }
      if (response.modeAskAttempts !== undefined) {
        setModeAskAttempts(response.modeAskAttempts);
        if (onModeAskAttempts) onModeAskAttempts(response.modeAskAttempts);
      }
      if (response.pendingCheckpoint !== undefined) {
        setPendingCheckpoint(response.pendingCheckpoint);
        if (onPendingCheckpoint) onPendingCheckpoint(response.pendingCheckpoint);
      }
      if (response.teacherStage !== undefined) {
        setTeacherStage(response.teacherStage);
        if (onTeacherStage) onTeacherStage(response.teacherStage);
      }
      setMessages(prev => [...prev, {
        sender: 'bot', kind: 'mentor',
        phase: response.phase, message: response.message,
        cards: response.suggested_cards || [], keywords: response.evidences || [],
        modeChoicePending: !!response.modeChoicePending,
        wrapUp: !!response.wrap_up,
        offerContact: !!response.offer_contact,
      }]);
      return;
    }

    if (typeof response === 'object' && response !== null &&
      (response.patterns?.length || response.next_moves?.length || response.questions?.length)) {
      setMessages(prev => [...prev, {
        sender: 'bot', kind: 'brainstorm',
        patterns: response.patterns || [],
        next_moves: response.next_moves || [],
        questions: response.questions || [],
      }]);
      return;
    }

    const botText = (typeof response === 'string' ? response : '') || vocab(lang).fallback_reply;
    setMessages(prev => [...prev, { text: botText, sender: 'bot' }]);
  };

  // User-initiated tier switch, available at any time (not just after a quota
  // popup). The local tier is the native engine (separate process) — no
  // WebGPU requirement — so switching to it just re-probes for the server.
  const switchTier = (tier) => {
    setModelTier(tier);
    if (tier === 'local') refreshEngine();
  };

  // ---- BYOK cloud mentor ----------------------------------------------------
  const handleSaveByok = () => {
    const key = (byokForm.key || '').trim();
    if (!looksLikeKey(byokForm.provider, key)) {
      setByokFormError(
        byokForm.provider === 'openai'
          ? t('byok.panel.errorOpenaiFormat')
          : t('byok.panel.errorGeminiFormat')
      );
      return;
    }
    saveByok({ provider: byokForm.provider, key, remember: byokForm.remember });
    setByok({ provider: byokForm.provider, key });
    setByokOff(false);
    setByokFormError(null);
    setByokForm((f) => ({ ...f, key: '' })); // never keep the raw key in component state
    setByokPanelOpen(false);
    setShowKeyNudge(false);
  };

  const handleByokModel = (model) => {
    const next = { ...byok, model: model === CLOUD_MODELS[byok.provider] ? undefined : model };
    saveByok({ ...next, remember: byokIsRemembered() });
    setByok(next);
  };

  const handleClearByok = () => {
    clearByok();
    setByok(null);
    setByokForm((f) => ({ ...f, key: '' }));
    setByokFormError(null);
  };

  const dismissKeyNudge = () => {
    nudgeDismissedRef.current = true;
    setShowKeyNudge(false);
  };

  const handleQuotaChoice = async (choice) => {
    setQuotaDialogMode(null);
    if (choice === 'cancel') { retryDataRef.current = null; return; }

    const newTier = choice; // 'local' | 'lite'
    setModelTier(newTier);

    const pending = retryDataRef.current;
    retryDataRef.current = null;
    if (!pending) return;

    if (pending.type === 'chat') {
      setIsLoading(true);
      const response = await sendCardData(pending.updatedMessages, newTier);
      setIsLoading(false);
      if (response) processApiResponse(response, pending.updatedMessages);
    }
  };

  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const draggedRef = useRef(false);
  const toggleChatbot = () => {
    if (draggedRef.current) { draggedRef.current = false; return; }
    setIsOpen(!isOpen);
  };
  const dragRef = useRef(null);

  return (
    <>
    <Draggable
      handle=".chatbot-header"
      cancel=".chatbot-header__controls, .mentor-model-select"
      bounds="parent"
      nodeRef={dragRef}
      disabled={isDocked}
      position={isDocked ? { x: 0, y: 0 } : dragPos}
      onStop={(e, data) => {
        draggedRef.current = Math.abs(data.x - dragPos.x) > 3 || Math.abs(data.y - dragPos.y) > 3;
        setDragPos({ x: data.x, y: data.y });
      }}
    >
    <div
      ref={dragRef}
      className={`chatbot${isOpen ? '' : ' is-collapsed'}${isDocked ? ' is-docked' : ''}`}
      style={{ zIndex: 1000 }}
    >
      <div className="chatbot-header" onClick={toggleChatbot}>
        <div className="mentor-id">
          <span className={`mentor-orb${isLoading ? ' is-thinking' : ''}`}>
            <Sparkle className="mentor-orb__icon" size={20} />
          </span>
          <span className="mentor-id__text">
            <span className="mentor-id__name">
              Mentor
              {useByok ? (
                <span className="mentor-tier-badge mentor-tier-badge--byok">
                  {byok.provider === 'openai' ? 'OpenAI' : 'Gemini'}
                </span>
              ) : modelTier !== 'cloud' && (
                <span className={`mentor-tier-badge ${TIER_STATIC[modelTier]?.badgeClass || 'mentor-tier-badge--local'}`}>
                  {modelTier === 'local' ? t('chat.header.tierLocal') : t('chat.header.tierLite')}
                </span>
              )}
            </span>
            <span className="mentor-id__status">
              {slmProgress
                ? slmProgress.text || t('chat.progress.downloadingModel')
                : useByok
                ? t('chat.header.statusByok', { provider: CLOUD_PROVIDER_LABELS[byok.provider] })
                : modelTier === 'local'
                ? (!engineAvailable
                    ? t('chat.header.statusEngineOff')
                    : engineDownloadPct != null
                    ? t('chat.header.statusDownloading', { pct: engineDownloadPct })
                    : !engineModelPresent
                    ? t('chat.header.statusModelNotDownloaded')
                    : (engineModelLabel || ENGINE_MODEL_LABEL))
                : modelTier === 'lite'
                ? 'Gemini Flash Lite'
                : 'Gemini 3 Flash'}
            </span>
          </span>
        </div>
        <div className="chatbot-header__controls">
          {/* "Motore AI" — connect your own Gemini/OpenAI key so slow PCs can
              offload the mentor to a big model. Key stays in this browser. */}
          <button
            type="button"
            className={`chatbot-ctrl mentor-byok-btn${useByok ? ' is-active' : ''}`}
            title={useByok ? t('byok.chatButton.activeTitle', { provider: CLOUD_PROVIDER_LABELS[byok.provider] }) : t('byok.chatButton.inactiveTitle')}
            onClick={(e) => { e.stopPropagation(); setByokPanelOpen((v) => !v); }}
          >
            <FontAwesomeIcon icon={faKey} />
          </button>
          {/* v1: local engine only — the whole tier switch is hidden. Re-enabled
              with CLOUD_TIERS_ENABLED in v2. */}
          {CLOUD_TIERS_ENABLED && (
            <div className="mentor-tier-switch" onClick={(e) => e.stopPropagation()}>
              {cloudEnabled && (
                <button
                  type="button"
                  className={`chatbot-ctrl mentor-tier-switch__btn${modelTier === 'cloud' ? ' is-active' : ''}`}
                  title={t('chat.header.tierCloudTooltip')}
                  onClick={() => switchTier('cloud')}
                >
                  <FontAwesomeIcon icon={faCloud} />
                </button>
              )}
              <button
                type="button"
                className={`chatbot-ctrl mentor-tier-switch__btn${modelTier === 'local' ? ' is-active' : ''}`}
                title={t('chat.header.tierLocalTooltip')}
                onClick={() => switchTier('local')}
              >
                <FontAwesomeIcon icon={faBolt} />
              </button>
              {cloudEnabled && (
                <button
                  type="button"
                  className={`chatbot-ctrl mentor-tier-switch__btn${modelTier === 'lite' ? ' is-active' : ''}`}
                  title={t('chat.header.tierLiteTooltip')}
                  onClick={() => switchTier('lite')}
                >
                  <FontAwesomeIcon icon={faFeather} />
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            className={`chatbot-ctrl chatbot-ctrl--brief${(brief && (brief.sintesi_processo || brief.obiettivo)) ? ' is-ready' : ''}`}
            title={t('chat.header.exportBriefTitle')}
            aria-label={t('chat.header.exportBriefAria')}
            onClick={(e) => { e.stopPropagation(); onGenerateBrief(); }}
          >
            {(brief && (brief.sintesi_processo || brief.obiettivo))
              ? <Sparkle size={14} />
              : <FontAwesomeIcon icon={faFileLines} />}
          </button>
          <button
            type="button"
            className="chatbot-ctrl"
            title={isDocked ? t('chat.header.undock') : t('chat.header.dock')}
            onClick={(e) => { e.stopPropagation(); setIsDocked((v) => !v); }}
          >
            <FontAwesomeIcon icon={isDocked ? faUpRightAndDownLeftFromCenter : faDownLeftAndUpRightToCenter} />
          </button>
          <span className="chatbot-caret">
            <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} />
          </span>
        </div>
      </div>

      {isOpen && (
        <>
          <>
          {(() => {
            const hasBrief = brief && (brief.evidenze_chiave?.length || brief.carte_collegate?.length || brief.direzione || brief.sintesi_processo);
            if (!hasBrief) return null;
            return (
            <div className="mentor-brief">
              <button
                type="button"
                className="mentor-brief__toggle"
                onClick={() => setBriefOpen(o => !o)}
              >
                {(briefOpen ? '▾' : '▸') + ' ' + t('chat.brief.toggle')}
              </button>
              {briefOpen && (
                  <div className="mentor-brief__body">
                  {brief.obiettivo && <p className="mentor-brief__goal"><strong>{t('chat.brief.goalLabel')}</strong> {brief.obiettivo}</p>}
                  {brief.sintesi_processo && (
                    <div className="mentor-brief__section mentor-brief__section--evidenze">
                      <span className="mentor-brief__label">{t('chat.brief.evidenceLabel')}</span>
                      <p className="mentor-brief__sintesi">{brief.sintesi_processo}</p>
                    </div>
                  )}
                  {brief.evidenze_chiave?.length > 0 && (
                    <div className="mentor-brief__section">
                      <span className="mentor-brief__label">{t('chat.brief.keywordsLabel')}</span>
                      {DT_PHASES.map(ph => {
                        const items = brief.evidenze_chiave.filter(e => e.phase === ph);
                        return items.length ? (
                          <div key={ph} className="mentor-brief__phase">
                            <em>{ph}:</em> {items.map((e, i) => <span key={i} className="kw-chip">{e.text}</span>)}
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                  {brief.carte_collegate?.length > 0 && (
                    <div className="mentor-brief__section">
                      <span className="mentor-brief__label">{t('chat.brief.linkedCardsLabel')}</span>
                      {brief.carte_collegate.map((c, i) => (
                        <span key={i} className={`kw-chip kw-chip--${c.by}`} title={c.perche}>{c.label} · {c.by}</span>
                      ))}
                    </div>
                  )}
                  {brief.direzione && <p className="mentor-brief__dir"><strong>{t('chat.brief.directionLabel')}</strong> {brief.direzione}</p>}
                  </div>
              )}
            </div>
            );
          })()}
          <div className="chatbot-messages">
            {messages.length === 0 ? (
              <div className="message bot">
                <MessageAvatar sender="bot" />
                <div className="message-body">{t('chat.messages.greeting')}</div>
              </div>
            ) : (
              messages.map((msg, index) => {
                if (msg.kind === 'mentor') {
                  const meta = PHASE_META(t)[msg.phase] || { name: msg.phase, color: '#7c5cff' };
                  return (
                    <div key={index} className="message bot">
                      <MessageAvatar sender="bot" />
                      <div className={`message-body mentor-msg${msg.cards.length > 0 ? ' has-card-ref' : ''}`}>
                        <span
                          className="phase-chip"
                          style={{ '--phase-color': meta.color }}
                          title={meta.name}
                          aria-label={t('chat.messages.phaseAria', { name: meta.name })}
                        >
                          <span className="phase-chip__dot" />
                        </span>
                        <p className="mentor-msg__text">{msg.message}</p>
                        {msg.cards.length > 0 && (
                          <CardRefBadge
                            cards={msg.cards}
                            isOpen={openCardBadge === index}
                            onToggle={() => setOpenCardBadge(openCardBadge === index ? null : index)}
                            t={t}
                          />
                        )}
                        {msg.keywords.length > 0 && (
                          <MsgSection title={t('chat.messages.savedKeywords')}>
                            <div className="chip-row">
                              {msg.keywords.map((k, i) => <span key={i} className="kw-chip">{k.text}</span>)}
                            </div>
                          </MsgSection>
                        )}
                        {msg.modeChoicePending && index === messages.length - 1 && (
                          <div className="mode-choice-row">
                            {MODE_CHOICES(t, lang).map((c) => (
                              <button
                                key={c.text}
                                type="button"
                                className="mode-choice-btn"
                                disabled={isLoading}
                                onClick={() => {
                                  // Switch the board layout immediately, without waiting
                                  // for the LLM round-trip — the button's text is one of
                                  // pickMode's own exact keyword matches (modeRouter.js),
                                  // so the server/local classifier is guaranteed to
                                  // resolve to this same mode; no need to wait for it to
                                  // confirm what we already know. handleSend's own
                                  // response.mode still runs afterwards and would correct
                                  // this if it ever somehow disagreed.
                                  setSessionMode(c.mode);
                                  if (onMode) onMode(c.mode);
                                  handleSend(c.text);
                                }}
                              >
                                {c.label}
                              </button>
                            ))}
                          </div>
                        )}
                        {msg.wrapUp && onRequestSaveSession && index === messages.length - 1 && (
                          <div className="mode-choice-row">
                            <button
                              type="button"
                              className="mode-choice-btn"
                              onClick={onRequestSaveSession}
                            >
                              {t('chat.messages.saveSession')}
                            </button>
                          </div>
                        )}
                        {msg.wrapUp && msg.offerContact && index === messages.length - 1 && (
                          <ContactRequestWidget defaultEmail={currentUser?.email || ''} getIdToken={getIdToken} sessionId={sessionId} />
                        )}
                      </div>
                    </div>
                  );
                }
                if (msg.kind === 'brainstorm') {
                  return (
                    <div key={index} className="message bot">
                      <MessageAvatar sender="bot" />
                      <div className="message-body mentor-msg">
                        {msg.patterns.length > 0 && (
                          <MsgSection title={t('chat.messages.patternsTitle')}>
                            <ul className="mentor-list">{msg.patterns.map((p, i) => <li key={i}>{p}</li>)}</ul>
                          </MsgSection>
                        )}
                        {msg.next_moves.length > 0 && (
                          <MsgSection title={t('chat.messages.nextMovesTitle')}>
                            <ul className="mentor-list">{msg.next_moves.map((m, i) => <li key={i}>{m}</li>)}</ul>
                          </MsgSection>
                        )}
                        {msg.questions.length > 0 && (
                          <MsgSection title={t('chat.messages.questionsTitle')}>
                            <ul className="mentor-list">{msg.questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
                          </MsgSection>
                        )}
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={index} className={`message ${msg.sender}`}>
                    <MessageAvatar sender={msg.sender} userInitial={userInitial} userColor={userColor} />
                    <div className="message-body">{msg.text}</div>
                  </div>
                );
              })
            )}
            {slmProgress && (
              <div className="message bot">
                <MessageAvatar sender="bot" />
                <div className="message-body">
                  <div className="slm-progress">
                    <div className="slm-progress__label">{slmProgress.text || t('chat.progress.downloadingModel')}</div>
                    <div className="slm-progress__bar">
                      <div
                        className={`slm-progress__fill${slmProgress.progress ? '' : ' slm-progress__fill--indeterminate'}`}
                        style={slmProgress.progress ? { transform: `scaleX(${slmProgress.progress})` } : undefined}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {isLoading && !slmProgress && longWait && (
              <div className="message bot">
                <MessageAvatar sender="bot" />
                <div className="message-body">
                  <div className="slm-progress">
                    <div className="slm-progress__label">{t('chat.messages.longWait', { verb: thinkingVerb })}</div>
                    <div className="slm-progress__bar">
                      <div className="slm-progress__fill slm-progress__fill--indeterminate" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {isLoading && !slmProgress && !longWait && (
              <div className="loading-dots">
                <span className="loading-dots__label">{thinkingVerb}</span>
                <span className="loading-dots__dot">.</span>
                <span className="loading-dots__dot">.</span>
                <span className="loading-dots__dot">.</span>
              </div>
            )}
            {showKeyNudge && !useByok && (
              <div className="mentor-nudge">
                <p className="mentor-nudge__text">
                  {t('byok.nudge.text')}
                </p>
                <div className="mentor-nudge__actions">
                  <button type="button" className="mentor-nudge__cta" onClick={() => { setShowKeyNudge(false); setByokPanelOpen(true); }}>
                    {t('byok.nudge.cta')}
                  </button>
                  <button type="button" className="mentor-nudge__dismiss" onClick={dismissKeyNudge}>
                    {t('byok.nudge.dismiss')}
                  </button>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          {byokPanelOpen && (
            <div className="byok-panel">
              <div className="byok-panel__head">
                <span className="byok-panel__title"><FontAwesomeIcon icon={faKey} /> {t('byok.panel.title')}</span>
                <button type="button" className="byok-panel__x" onClick={() => setByokPanelOpen(false)} aria-label={t('byok.panel.closeAria')}>×</button>
              </div>
              {useByok ? (
                <>
                  <p className="byok-panel__status">
                    {t('byok.panel.activeLabel')} · <strong>{CLOUD_PROVIDER_LABELS[byok.provider]}</strong> · <code>{maskKey(byok.key)}</code>
                  </p>
                  <p className="byok-panel__note">
                    {t('byok.panel.activeNote', { provider: CLOUD_PROVIDER_LABELS[byok.provider] })}
                  </p>
                  <label className="byok-panel__remember">
                    {t('byok.panel.modelLabel')}{' '}
                    <select className="byok-panel__input" value={byok.model || CLOUD_MODELS[byok.provider]}
                      onChange={(e) => handleByokModel(e.target.value)}>
                      {[...new Set([byok.model || CLOUD_MODELS[byok.provider], ...(byokModels || [])])].map((id) => (
                        <option key={id} value={id}>
                          {id === CLOUD_MODELS[byok.provider] ? `${id} (${t('byok.panel.modelStandard')})` : id}
                        </option>
                      ))}
                    </select>
                  </label>
                  {!byokModels && <p className="byok-panel__note">{t('byok.panel.modelListUnavailable')}</p>}
                  <div className="byok-panel__actions">
                    <button type="button" className="byok-panel__btn" onClick={() => setByokOff(true)}>{t('byok.panel.useLocal')}</button>
                    <button type="button" className="byok-panel__btn byok-panel__btn--danger" onClick={handleClearByok}>{t('byok.panel.removeKey')}</button>
                  </div>
                </>
              ) : (
                <>
                  {byok && byokOff && (
                    <p className="byok-panel__status">
                      {t('byok.panel.savedOff', { provider: CLOUD_PROVIDER_LABELS[byok.provider] })}{' '}
                      <button type="button" className="byok-panel__link" onClick={() => setByokOff(false)}>{t('byok.panel.reactivate')}</button>
                    </p>
                  )}
                  <div className="byok-panel__providers">
                    <label>
                      <input type="radio" name="byok-provider" checked={byokForm.provider === 'gemini'}
                        onChange={() => setByokForm(f => ({ ...f, provider: 'gemini' }))} /> Google Gemini
                    </label>
                    <label>
                      <input type="radio" name="byok-provider" checked={byokForm.provider === 'openai'}
                        onChange={() => setByokForm(f => ({ ...f, provider: 'openai' }))} /> OpenAI
                    </label>
                  </div>
                  <input
                    type="password"
                    className="byok-panel__input"
                    placeholder={byokForm.provider === 'openai' ? 'sk-…' : 'AIza…'}
                    value={byokForm.key}
                    onChange={(e) => { setByokForm(f => ({ ...f, key: e.target.value })); setByokFormError(null); }}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <label className="byok-panel__remember">
                    <input type="checkbox" checked={byokForm.remember}
                      onChange={(e) => setByokForm(f => ({ ...f, remember: e.target.checked }))} />
                    {t('byok.panel.rememberLabel')}
                  </label>
                  {byokFormError && <p className="byok-panel__err">{byokFormError}</p>}
                  <p className="byok-panel__note">
                    {t('byok.panel.privacyNote', {
                      storage: byokForm.remember ? 'localStorage' : t('byok.panel.storageSession'),
                      target: byokForm.provider === 'openai' ? 'api.openai.com' : 'Google',
                    })}
                  </p>
                  <button type="button" className="byok-panel__save" onClick={handleSaveByok}>{t('byok.panel.save')}</button>
                </>
              )}
            </div>
          )}
          {modelTier === 'local' && !engineAvailable && !useByok && (
            <div className="engine-setup">
              <div className="engine-setup__title">{t('chat.engineSetup.title')}</div>
              <p className="engine-setup__text">
                {t('chat.engineSetup.descBefore')}{' '}
                <strong>Sinergie Engine</strong>{t('chat.engineSetup.descAfter')}
              </p>
              <ol className="engine-setup__steps">
                <li>
                  <a
                    className="engine-setup__dl"
                    href={ENGINE_DOWNLOAD_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('chat.engineSetup.downloadLink')}
                  </a>
                </li>
                <li>{t('chat.engineSetup.step2')}</li>
                <li>{t('chat.engineSetup.step3')}</li>
              </ol>
              <p className="engine-setup__note">
                {t('chat.engineSetup.note')}
              </p>
              <button type="button" className="engine-setup__retry" onClick={refreshEngine}>
                {t('chat.engineSetup.retryButton')}
              </button>
            </div>
          )}
          <div className="chatbot-input">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder={
                !useByok && modelTier === 'local' && !engineAvailable
                  ? t('chat.input.placeholderDisabled')
                  : t('chat.input.placeholder')
              }
              className="input-field"
              disabled={!useByok && modelTier === 'local' && !engineAvailable}
            />
            <button
              onClick={() => handleSend()}
              className="send-button"
              disabled={!useByok && modelTier === 'local' && !engineAvailable}
            >
              {t('chat.input.send')}
            </button>
          </div>
          </>
        </>
      )}

    </div>
    </Draggable>
    {quotaDialogMode && ReactDOM.createPortal(
      <QuotaDialog
        mode={quotaDialogMode}
        reason={quotaDialogReason}
        onChoice={handleQuotaChoice}
        slmModelName={slmModel.label || slmModel.modelId}
        slmModelSize={slmModel.size || ''}
      />,
      document.body
    )}
    {errorPopup && ReactDOM.createPortal(
      <div className="error-popup-backdrop" onClick={() => setErrorPopup(null)}>
        <div className="error-popup" onClick={(e) => e.stopPropagation()}>
          <div className="error-popup__icon">!</div>
          <div className="error-popup__code">{t('chat.errorPopup.codeLabel', { code: errorPopup.code })}</div>
          <div className="error-popup__msg">{errorPopup.message}</div>
          {!errorPopup.onRetry && <div className="error-popup__hint">{t('chat.errorPopup.contactAdmin')}</div>}
          <div className="error-popup__actions">
            {errorPopup.onRetry && (
              <button className="error-popup__btn error-popup__btn--retry" onClick={errorPopup.onRetry}>
                {t('chat.errorPopup.retry')}
              </button>
            )}
            {errorPopup.onClearRetry && (
              <button className="error-popup__btn error-popup__btn--clear" onClick={errorPopup.onClearRetry}>
                {t('chat.errorPopup.clearRetry')}
              </button>
            )}
            <button className="error-popup__btn error-popup__btn--close" onClick={() => setErrorPopup(null)}>
              {t('chat.errorPopup.close')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

export default Chatbot;
